/** The Firestore projection of a design; its files live as workspace files under `folder`. Mirrors the server's `Design`. */
export interface Design {
  id: string
  name: string
  folder: string
  currentSnapshotId: string | null
  /** The latest render version; `null` until the design has rendered. */
  renderVersion: number | null
  /** The storage generation of `design.html`; `null` until the file is written. */
  revision: string | null
  createdAt: Date
  updatedAt: Date
}

export const DESIGN_RUN_STATUSES = ['running', 'completed', 'failed'] as const

/** A design run's state. */
export type DesignRunStatus = (typeof DESIGN_RUN_STATUSES)[number]

export const DESIGN_RUN_STAGES = ['plan', 'render', 'attach'] as const

/** The step a design run is on. */
export type DesignRunStage = (typeof DESIGN_RUN_STAGES)[number]

export const DESIGN_RUN_ERROR_CODES = [
  'design_run_enqueue_failed',
  'design_run_failed',
  'design_render_failed',
  'design_attach_failed',
  'run_timed_out',
] as const

/** Why a design run failed. */
export type DesignRunErrorCode = (typeof DESIGN_RUN_ERROR_CODES)[number]

/** Whether the run is still making its design. */
export function isDesignRunLive(run: Pick<DesignRun, 'status'> | null): boolean {
  return run?.status === 'running'
}

/** Whether the run finished its design. */
export function isDesignRunMade(run: Pick<DesignRun, 'status'> | null): boolean {
  return run?.status === 'completed'
}

export interface DesignRun {
  id: string
  designId: string
  postId: string
  status: DesignRunStatus
  stage: DesignRunStage
  /** Why the run failed; `null` unless it failed. */
  errorCode: DesignRunErrorCode | null
  requestedBy: string
  /** The render version the run made; `null` until it has rendered. */
  renderVersion: number | null
  /** The pages of that render; `null` until it has rendered. */
  pageCount: number | null
  createdAt: Date
  updatedAt: Date
  completedAt: Date | null
}

export const DESIGN_EDIT_RUN_STATUSES = ['running', 'completed', 'failed', 'cancelled'] as const

/** A design edit run's state. */
export type DesignEditRunStatus = (typeof DESIGN_EDIT_RUN_STATUSES)[number]

export const DESIGN_EDIT_RUN_STAGES = ['read', 'edit', 'check'] as const

/** The step a design edit run is on. */
export type DesignEditRunStage = (typeof DESIGN_EDIT_RUN_STAGES)[number]

/** One Raldo edit of one page of a design. Mirrors the server's `DesignEditRun`. */
export interface DesignEditRun {
  id: string
  designId: string
  page: number
  /** The element the edit was aimed at; `null` when the page was. */
  eid: string | null
  instruction: string
  status: DesignEditRunStatus
  stage: DesignEditRunStage
  /** Why the run failed; `null` unless it did. */
  errorCode: string | null
  revisionBefore: string
  /** The design revision the edit wrote; `null` until it has. */
  revisionAfter: string | null
  summary: string | null
  changedEid: string | null
  undone: boolean
  createdAt: Date
  completedAt: Date | null
}

/** Whether Raldo is still editing. */
export function isDesignEditRunLive(run: Pick<DesignEditRun, 'status'> | null): boolean {
  return run?.status === 'running'
}

/** Whether a finished edit can be undone: it landed, was not undone yet, and nothing has changed the design since. */
export function canUndoDesignEditRun(
  run: Pick<DesignEditRun, 'status' | 'undone' | 'revisionAfter'>,
  designRevision: string | null,
): boolean {
  return (
    run.status === 'completed' &&
    !run.undone &&
    run.revisionAfter !== null &&
    run.revisionAfter === designRevision
  )
}

/** The fonts a brand style may use: the template's own, or the brand font. */
export const BRAND_STYLE_FONTS = ['template', 'brand'] as const

export type BrandStyleFont = (typeof BRAND_STYLE_FONTS)[number]

/** The template a brand uses until it picks one. */
export const DEFAULT_BRAND_STYLE_TEMPLATE_ID = 't09'

/** How many own designs one pick may name. */
export const OWN_DESIGNS_MAX_ITEMS = 10

/** The brand's saved style, `style/style.json` in its workspace. Mirrors the server's `BrandStyle`. */
export interface BrandStyle {
  templateId: string
  /** The accent colour; `null` takes the first brand colour. */
  accent: string | null
  font: BrandStyleFont
  ownDesigns: { libraryItemIds: string[] } | null
  /** The design a made brand style was drawn from; absent while the style is a library template. */
  madeFrom?: string
}

/** The founder's pick on the Visual style step. Mirrors the server's `VisualStyleChoice`. */
export type VisualStyleChoice =
  | { kind: 'template'; templateId: string; accent: string | null; font: BrandStyleFont }
  | { kind: 'own-designs'; libraryItemIds: string[] }

/** Why `confirmVisualStyle` refused. */
export type ConfirmVisualStyleReason =
  | 'visual_style_strategy_missing'
  | 'visual_style_designs_missing'
  | 'owner_admin_role_required'
